import React, { useState, useEffect } from 'react';
import { User } from 'firebase/auth';
import { signInWithGoogle, signOutUser, onAuthStateChange } from '../../services/firebase';

interface AuthModalProps {
    show: boolean;
    onClose: () => void;
    onAuthSuccess: (user: User) => void;
}

export const AuthModal: React.FC<AuthModalProps> = ({ show, onClose, onAuthSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleGoogleSignIn = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const user = await signInWithGoogle();
            if (user) {
                onAuthSuccess(user);
                onClose();
            }
        } catch (err) {
            setError('Failed to sign in with Google. Please try again.');
            console.error('Google sign-in error:', err);
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    const styles = {
        overlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
        },
        modal: {
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '40px',
            width: '400px',
            color: '#fff',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            textAlign: 'center' as const
        },
        title: {
            fontSize: '24px',
            fontWeight: 'bold',
            marginBottom: '8px',
            color: '#4ECDC4'
        },
        subtitle: {
            fontSize: '16px',
            color: '#ccc',
            marginBottom: '32px'
        },
        signInButton: {
            backgroundColor: '#4285f4',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            cursor: 'pointer',
            width: '100%',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            transition: 'background-color 0.2s'
        },
        signInButtonHover: {
            backgroundColor: '#3367d6'
        },
        error: {
            color: '#ff6b6b',
            fontSize: '14px',
            marginTop: '16px',
            textAlign: 'left' as const
        },
        closeButton: {
            position: 'absolute' as const,
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            color: '#ccc',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '4px'
        },
        features: {
            textAlign: 'left' as const,
            marginTop: '24px',
            fontSize: '14px',
            color: '#999'
        },
        featureList: {
            listStyle: 'none',
            padding: 0,
            margin: '12px 0 0 0'
        },
        featureItem: {
            padding: '4px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        }
    };

    return (
        <div style={styles.overlay}>
            <div style={{ ...styles.modal, position: 'relative' }}>
                <button style={styles.closeButton} onClick={onClose}>×</button>
                
                <h2 style={styles.title}>Welcome to 3D Presenter</h2>
                <p style={styles.subtitle}>
                    You can use the app without an account! 
                    <br />
                    Sign in only when you want to save presentations to the cloud.
                </p>
                
                <button 
                    style={styles.signInButton}
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                    onMouseEnter={(e) => {
                        if (!loading) {
                            e.currentTarget.style.backgroundColor = '#3367d6';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!loading) {
                            e.currentTarget.style.backgroundColor = '#4285f4';
                        }
                    }}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24">
                        <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {loading ? 'Signing in...' : 'Sign in with Google'}
                </button>

                {error && <p style={styles.error}>{error}</p>}
                
                {/* 🎯 PROFESSIONAL GUEST MODE: Add guest button */}
                <button 
                    style={{
                        backgroundColor: 'transparent',
                        color: '#ccc',
                        border: '1px solid #444',
                        borderRadius: '8px',
                        padding: '12px 24px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        width: '100%',
                        marginBottom: '24px',
                        transition: 'all 0.2s'
                    }}
                    onClick={onClose}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#ccc';
                    }}
                >
                    Continue as Guest
                </button>

                <div style={styles.features}>
                    <div style={{ marginBottom: '16px' }}>
                        <strong style={{ color: '#4ECDC4' }}>Available right now (Guest Mode):</strong>
                        <ul style={styles.featureList}>
                            <li style={styles.featureItem}>
                                <span>✨</span> Create and edit 3D presentations
                            </li>
                            <li style={styles.featureItem}>
                                <span>🎨</span> Full design and layout tools
                            </li>
                            <li style={styles.featureItem}>
                                <span>📱</span> Download presentations locally
                            </li>
                        </ul>
                    </div>
                    
                    <strong>With your account you also get:</strong>
                    <ul style={styles.featureList}>
                        <li style={styles.featureItem}>
                            <span>☁️</span> Cloud storage for your presentations
                        </li>
                        <li style={styles.featureItem}>
                            <span>🔄</span> Sync across devices
                        </li>
                        <li style={styles.featureItem}>
                            <span>👥</span> Share presentations with others
                        </li>
                        <li style={styles.featureItem}>
                            <span>📈</span> Version history & backups
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

// User Profile Component for when signed in
interface UserProfileProps {
    user: User;
    onSignOut: () => void;
}

export const UserProfile: React.FC<UserProfileProps> = ({ user, onSignOut }) => {
    const [showDropdown, setShowDropdown] = useState(false);

    const handleSignOut = async () => {
        try {
            await signOutUser();
            onSignOut();
            setShowDropdown(false);
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    // 🎯 PROFESSIONAL UI: Use CSS classes for clean, modern design
    const getInitials = (name: string): string => {
        return name
            .split(' ')
            .map(word => word.charAt(0))
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    const displayName = user.displayName || user.email?.split('@')[0] || 'User';
    const initials = getInitials(displayName);

    return (
        <div className="user-profile">
            <div className="user-profile-dropdown" onClick={() => setShowDropdown(!showDropdown)}>
                {user.photoURL ? (
                    <img 
                        src={user.photoURL} 
                        alt="Profile" 
                        className="user-profile-avatar"
                        data-html2canvas-ignore="true"
                    />
                ) : (
                    <div className="user-profile-avatar" style={{ 
                        backgroundColor: '#4ECDC4', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: '#000'
                    }}>
                        {initials}
                    </div>
                )}
                
                <div className="user-profile-info">
                    <div className="user-profile-name">{displayName}</div>
                    <div className="user-profile-email">{user.email}</div>
                </div>
                
                <span style={{ fontSize: '12px', color: '#ccc', marginLeft: '8px' }}>
                    {showDropdown ? '▲' : '▼'}
                </span>
            </div>

            {showDropdown && (
                <div className="user-profile-menu">
                    <button 
                        className="user-profile-menu-item"
                        onClick={handleSignOut}
                        style={{ color: '#ff6b6b' }}
                    >
                        🚪 Sign Out
                    </button>
                </div>
            )}
        </div>
    );
}; 